/**
 * Matrix Filter Classification
 *
 * Provides classification logic for hexagon data visualization.
 * Supports two classification modes:
 *
 * 1. Matrix (2D) Classification:
 *    - Uses two properties: one for "bands" (rows) and one for "thresholds" (columns)
 *    - Each band has its own threshold boundaries
 *    - Example: Height bands + Density thresholds (Allskog style)
 *
 * 2. Simple (1D) Classification:
 *    - Uses a single property with min/max ranges per class
 *    - Example: Volume-based classification with Low/Medium/High classes
 *
 * @module matrixClassification
 */

import { SQUARE_METERS_PER_HECTARE } from '@utils/measurementSystem';

/** Default label for the custom price/m² field */
export const DEFAULT_CUSTOM_PRICE_LABEL = 'Custom price/m²';

/**
 * Available hexagon properties that can be used for classification.
 * Each property maps to a field in hexagon metadata.
 */
export const AVAILABLE_PROPERTIES = [
	{ id: 'mean_height', label: 'Mean Height (m)', unit: 'm' },
	{ id: 'count', label: 'Count', unit: 'pcs' },
	{ id: 'density', label: 'Density (/ha)', unit: '/ha' },
	{ id: 'mean_crown_diameter', label: 'Crown Diameter (m)', unit: 'm' },
	{ id: 'total_area', label: 'Total Area (m²)', unit: 'm²' },
	{ id: 'confidence', label: 'Mean Confidence', unit: '%' },
	{ id: 'hectare_equivalent', label: 'Hectare Equivalent', unit: '/ha' },
	// Stem estimation properties (enriched from detections[class].estimated_stems)
	{ id: 'stems_low', label: 'Stem Estimate (Low)', unit: 'pcs' },
	{ id: 'stems_medium', label: 'Stem Estimate (Medium)', unit: 'pcs' },
	{ id: 'stems_high', label: 'Stem Estimate (High)', unit: 'pcs' },
	{ id: 'stems_per_ha_low', label: 'Stems/ha (Low)', unit: '/ha' },
	{ id: 'stems_per_ha_medium', label: 'Stems/ha (Medium)', unit: '/ha' },
	{ id: 'stems_per_ha_high', label: 'Stems/ha (High)', unit: '/ha' },
	// Volume estimation properties
	{ id: 'volume_m3', label: 'Volume (m³)', unit: 'm³' },
	{ id: 'volume_per_ha_m3', label: 'Volume/ha (m³/ha)', unit: 'm³/ha' },
	{ id: 'basal_area_m2', label: 'Basal Area (m²)', unit: 'm²' },
];

/**
 * Classify a hexagon using a matrix filter
 * @param {object} hexagonMeta - Hexagon metadata object
 * @param {object} filter - Matrix filter configuration
 * @returns {string|null} - Class ID or null
 */
export function classifyWithMatrixFilter(hexagonMeta, filter) {
	if (!filter?.bands?.length || !filter?.thresholds?.length) {
		return null;
	}

	// Get values for both axes
	const bandValue = getPropertyValue(hexagonMeta, filter.bandProperty);
	const thresholdValue = getPropertyValue(hexagonMeta, filter.thresholdProperty);

	if (bandValue === null || thresholdValue === null) {
		return null;
	}

	// Find closest band
	let bandIndex = 0;
	let minDiff = Math.abs(bandValue - filter.bands[0]);

	for (let i = 1; i < filter.bands.length; i++) {
		const diff = Math.abs(bandValue - filter.bands[i]);
		if (diff < minDiff) {
			minDiff = diff;
			bandIndex = i;
		}
	}

	const threshold = filter.thresholds[bandIndex];
	if (!threshold) return null;

	// Check minimum value
	if (thresholdValue < threshold.min) {
		return 'below_minimum';
	}

	// Check maximum value (if defined)
	if (threshold.max !== undefined && thresholdValue > threshold.max) {
		return 'unclassified';
	}

	// Find class based on boundaries
	// Each class has a boundary that defines its upper limit
	for (let i = 0; i < filter.classes.length; i++) {
		const boundary = threshold.boundaries[i];
		// If boundary exists and value is within it, return this class
		if (boundary !== undefined && thresholdValue <= boundary) {
			return filter.classes[i]?.id || null;
		}
	}

	// Above all boundaries = unclassified
	return 'unclassified';
}

/**
 * Get property value from hexagon metadata
 * Supports computed properties like density
 */
export function getPropertyValue(meta, propertyId) {
	if (!meta || !propertyId) return null;

	switch (propertyId) {
		case 'mean_height':
			return meta.mean_height ?? null;
		case 'count':
			return meta.count ?? null;
		case 'density':
			// If density is pre-calculated, use it
			if (meta.density !== undefined) return meta.density;
			// Compute density from count and area (per-hectare: 1 ha = 10,000 m²)
			// Cannot infer density without area information
			if (meta.count !== undefined && meta.area !== undefined && meta.area > 0) {
				return (meta.count / meta.area) * SQUARE_METERS_PER_HECTARE;
			}
			return null; // Cannot compute density without area
		case 'hectare_equivalent':
			// If hectare_equivalent is provided by the backend, use it
			if (meta.hectare_equivalent !== undefined) return meta.hectare_equivalent;
			// Compute from count and area: extrapolate detections to per-hectare
			if (meta.count !== undefined && meta.area !== undefined && meta.area > 0) {
				return (meta.count / meta.area) * SQUARE_METERS_PER_HECTARE;
			}
			return null;
		case 'mean_crown_diameter':
			return meta.mean_crown_diameter ?? meta.crown_diameter ?? null;
		case 'total_area':
			return meta.total_area ?? meta.area ?? null;
		case 'confidence':
			return meta.confidence ?? meta.mean_confidence ?? null;
		// Volume estimation properties
		case 'volume_m3':
			return meta.volume_m3 ?? null;
		case 'volume_per_ha_m3':
			return meta.volume_per_ha_m3 ?? null;
		case 'basal_area_m2':
			return meta.basal_area_m2 ?? null;
		default:
			return meta[propertyId] ?? null;
	}
}

/**
 * Get class info by ID
 */
export function getClassById(classId, filter) {
	if (classId === 'below_minimum') {
		return {
			id: 'below_minimum',
			label: 'Below Minimum',
			color: { r: 128, g: 128, b: 128, a: 0.5 },
		};
	}
	if (classId === 'unclassified') {
		return {
			id: 'unclassified',
			label: 'Unclassified',
			color: { r: 100, g: 100, b: 100, a: 0.3 },
		};
	}
	return filter.classes.find(c => c.id === classId) || null;
}

/**
 * Convert hex color string to RGBA object
 */
export function hexToRgba(hex, alpha = 0.7) {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result
		? {
				r: parseInt(result[1], 16),
				g: parseInt(result[2], 16),
				b: parseInt(result[3], 16),
				a: alpha,
			}
		: { r: 128, g: 128, b: 128, a: alpha };
}

/**
 * Convert RGBA object to hex string
 */
export function rgbaToHex(rgba) {
	const toHex = c => c.toString(16).padStart(2, '0');
	return `#${toHex(rgba.r)}${toHex(rgba.g)}${toHex(rgba.b)}`;
}

/**
 * Id of the pseudo-currency used when prices are plain units. It has no symbol
 * and its id is not a real currency code, so nothing is printed for it.
 */
export const UNIT_CURRENCY_ID = 'UNIT';

/**
 * Available currencies for pricing calculations.
 * First entry (UNIT) is used as fallback when no currency is specified.
 * `flag` is the emoji flag shown in compact pickers where only a symbol fits —
 * it's what tells NOK and SEK apart, since both use "kr", and likewise USD and
 * CAD, which share "$".
 *
 * @type {Array<{id: string, symbol: string, label: string, flag: string}>}
 */
export const CURRENCIES = [
	{ id: 'UNIT', symbol: '', label: 'Units (no currency)', flag: '' },
	{ id: 'NOK', symbol: 'kr', label: 'Norwegian Krone (NOK)', flag: '🇳🇴' },
	{ id: 'EUR', symbol: '€', label: 'Euro (EUR)', flag: '🇪🇺' },
	{ id: 'USD', symbol: '$', label: 'US Dollar (USD)', flag: '🇺🇸' },
	{ id: 'CAD', symbol: '$', label: 'Canadian Dollar (CAD)', flag: '🇨🇦' },
	{ id: 'SEK', symbol: 'kr', label: 'Swedish Krona (SEK)', flag: '🇸🇪' },
	{ id: 'GBP', symbol: '£', label: 'British Pound (GBP)', flag: '🇬🇧' },
];

/**
 * Currency marker for a monetary total in an exported report: the symbol, then
 * the currency code in brackets, e.g. "$ (CAD)" or "kr (NOK)". The brackets keep
 * the code reading as an annotation of the symbol rather than as part of the
 * figure.
 *
 * Symbols alone are ambiguous — USD/CAD both print "$", NOK/SEK both print
 * "kr" — and a report has none of the on-screen picker's context (no flag, no
 * label) to settle which one a figure is denominated in. The code is therefore
 * stated where it matters most: beside the total the reader takes away.
 *
 * Degrades to whatever is available, since report callers pass a currency that
 * may be partially resolved: units (no symbol, no real code) print nothing, and
 * a currency known only by its symbol prints just the symbol.
 *
 * @param {{id?: string, symbol?: string}} [currency]
 * @returns {string} e.g. "$ (CAD)", "€ (EUR)", or '' for units
 */
export function formatCurrencyMarker(currency) {
	const symbol = currency?.symbol ?? '';
	const code = currency?.id && currency.id !== UNIT_CURRENCY_ID ? currency.id : '';
	return [symbol, code && `(${code})`].filter(Boolean).join(' ');
}

/**
 * A monetary total as printed in a report: the amount to two decimals followed
 * by the currency marker, e.g. "1,350.00 kr (NOK)". Plain units print the bare
 * amount, with no dangling separator.
 *
 * @param {number} amount
 * @param {{id?: string, symbol?: string}} [currency]
 * @returns {string}
 */
export function formatCurrencyTotal(amount, currency) {
	const value = (amount ?? 0).toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
	const marker = formatCurrencyMarker(currency);
	return marker ? `${value} ${marker}` : value;
}

/**
 * Create an empty matrix (2D) filter template.
 * Used as starting point for new matrix filters.
 * @returns {Object} Empty matrix filter configuration
 */
export function createEmptyMatrixFilter() {
	return {
		name: 'New Matrix Filter',
		type: 'matrix',
		bandProperty: 'mean_height',
		thresholdProperty: 'count',
		currency: 'EUR',
		customPriceFields: [],
		bands: [2, 3, 4, 5, 6],
		classes: [
			{ id: 'class_1', label: 'Easy', color: '#4ade80', opacity: 0.7, pricePerM2: 0 },
			{ id: 'class_2', label: 'Medium', color: '#fbbf24', opacity: 0.7, pricePerM2: 0 },
			{ id: 'class_3', label: 'Hard', color: '#ef4444', opacity: 0.7, pricePerM2: 0 },
		],
		thresholds: [
			{ min: 200, max: 1000, boundaries: [400, 700, 1000] },
			{ min: 200, max: 900, boundaries: [350, 600, 900] },
			{ min: 200, max: 800, boundaries: [300, 500, 800] },
			{ min: 200, max: 700, boundaries: [250, 450, 700] },
			{ min: 150, max: 600, boundaries: [200, 350, 600] },
		],
	};
}

/**
 * Default matrix (2D) filter preset with 5 difficulty classes.
 * Configured for height bands with density thresholds.
 * @type {Object}
 */
export const DEFAULT_PRESET = {
	name: 'Default Classification',
	type: 'matrix',
	bandProperty: 'mean_height',
	thresholdProperty: 'density',
	currency: 'EUR',
	customPriceFields: [],
	bands: [2, 3, 4, 5, 6],
	classes: [
		{ id: 'very_easy', label: 'Very Easy', color: '#90EE90', opacity: 0.7, pricePerM2: 0.5 },
		{ id: 'easy', label: 'Easy', color: '#FFFF00', opacity: 0.7, pricePerM2: 1.0 },
		{ id: 'medium', label: 'Medium', color: '#FFA500', opacity: 0.7, pricePerM2: 1.5 },
		{ id: 'hard', label: 'Hard', color: '#FF6347', opacity: 0.7, pricePerM2: 2.0 },
		{ id: 'very_hard', label: 'Very Hard', color: '#FF0000', opacity: 0.7, pricePerM2: 3.0 },
	],
	thresholds: [
		{ min: 2500, max: 30000, boundaries: [6000, 10000, 15000, 20000, 30000] },
		{ min: 2500, max: 25000, boundaries: [5000, 8000, 12000, 16000, 25000] },
		{ min: 2000, max: 20000, boundaries: [4000, 6500, 10000, 13000, 20000] },
		{ min: 2000, max: 15000, boundaries: [3500, 5500, 8500, 11000, 15000] },
		{ min: 1500, max: 12000, boundaries: [3000, 4500, 7000, 9500, 12000] },
	],
};

/**
 * Create a simple (1D) filter template.
 * Uses a single property with min/max ranges per class.
 * @param {string} property - Property ID to classify by (default: 'volume_m3')
 * @returns {Object} Simple filter configuration
 */
export function createSimpleFilter(property = 'volume_m3') {
	return {
		name: 'New Simple Filter',
		type: 'simple',
		property: property,
		currency: 'EUR',
		customPriceFields: [],
		classes: [
			{
				id: 'class_1',
				label: 'Low',
				color: '#4ade80',
				opacity: 0.7,
				pricePerM2: 0,
				min: 0,
				max: 5,
			},
			{
				id: 'class_2',
				label: 'Medium',
				color: '#fbbf24',
				opacity: 0.7,
				pricePerM2: 0,
				min: 5,
				max: 10,
			},
			{
				id: 'class_3',
				label: 'High',
				color: '#ef4444',
				opacity: 0.7,
				pricePerM2: 0,
				min: 10,
				max: null,
			},
		],
	};
}

/**
 * Default simple (1D) filter preset for volume classification.
 * 5 classes from Very Low to Very High based on volume_m3.
 * @type {Object}
 */
export const DEFAULT_SIMPLE_PRESET = {
	name: 'Volume Classification',
	type: 'simple',
	property: 'volume_m3',
	currency: 'EUR',
	customPriceFields: [],
	classes: [
		{
			id: 'very_low',
			label: 'Very Low',
			color: '#90EE90',
			opacity: 0.7,
			pricePerM2: 0,
			min: 0,
			max: 2,
		},
		{ id: 'low', label: 'Low', color: '#FFFF00', opacity: 0.7, pricePerM2: 0, min: 2, max: 5 },
		{
			id: 'medium',
			label: 'Medium',
			color: '#FFA500',
			opacity: 0.7,
			pricePerM2: 0,
			min: 5,
			max: 10,
		},
		{
			id: 'high',
			label: 'High',
			color: '#FF6347',
			opacity: 0.7,
			pricePerM2: 0,
			min: 10,
			max: 20,
		},
		{
			id: 'very_high',
			label: 'Very High',
			color: '#FF0000',
			opacity: 0.7,
			pricePerM2: 0,
			min: 20,
			max: null,
		},
	],
};

/**
 * Classify a hexagon using a simple (1D) filter
 * @param {object} hexagonMeta - Hexagon metadata object
 * @param {object} filter - Simple filter configuration
 * @returns {string|null} - Class ID or null
 */
export function classifyWithSimpleFilter(hexagonMeta, filter) {
	if (!filter?.classes?.length || !filter?.property) {
		return null;
	}

	const value = getPropertyValue(hexagonMeta, filter.property);
	if (value === null) {
		return 'unclassified';
	}

	// Find class based on min/max ranges
	for (const cls of filter.classes) {
		const min = cls.min ?? 0;
		const max = cls.max ?? Infinity;

		if (value >= min && (cls.max === null || value <= max)) {
			return cls.id;
		}
	}

	return 'unclassified';
}

/**
 * Computes gaps between a Simple (1D) filter's class ranges and the actual
 * data range for the selected property. A "gap" is a sub-range of real data
 * values that no class's [min, max] interval covers, meaning any feature
 * whose value falls there will be classified as "Unclassified" even though
 * its data is fully valid.
 *
 * The last class is treated as open-ended (covers up to +Infinity) whenever
 * its `max` is null/undefined — matching the runtime behavior of
 * classifyWithSimpleFilter. A finite `max` on the last class is NOT
 * special-cased here; if it's below the data's actual max, that's reported
 * as a real gap (this is the scenario that silently strands high-value
 * features as unclassified).
 *
 * Classes conventionally use whole-number boundaries one unit apart (e.g.
 * "0-250" followed by "251-600") to express contiguous coverage of
 * integer-like data. Since the underlying property values can be
 * non-integer (e.g. hectare equivalent), a strict interval merge would flag
 * the sub-unit sliver between 250 and 251 as a "gap" on every normally
 * configured filter. `adjacencyTolerance` absorbs boundaries within that
 * distance of each other so only real, user-visible gaps are reported.
 *
 * @param {Array} classes - filter.classes array (each with min, max)
 * @param {{min: number, max: number, hasData: boolean}} propertyRange - Actual data range
 * @param {number} [adjacencyTolerance=1] - Max distance between a class's max
 *   and the next class's min to still be treated as contiguous
 * @returns {{hasGaps: boolean, gaps: Array<{min: number, max: number}>, coveragePercent: number}}
 */
export function getSimpleFilterCoverageGaps(classes, propertyRange, adjacencyTolerance = 1) {
	if (!classes?.length || !propertyRange?.hasData) {
		return { hasGaps: false, gaps: [], coveragePercent: 100 };
	}

	const { min: dataMin, max: dataMax } = propertyRange;
	if (dataMin > dataMax) {
		return { hasGaps: false, gaps: [], coveragePercent: 100 };
	}

	// Normalize + sort class intervals by min. Open-ended max (null/undefined) -> +Infinity.
	const intervals = classes
		.map(cls => ({
			min: cls.min ?? 0,
			max: cls.max === null || cls.max === undefined ? Infinity : cls.max,
		}))
		.filter(iv => iv.max >= iv.min)
		.sort((a, b) => a.min - b.min);

	// Merge overlapping/adjacent (within tolerance) intervals
	const merged = [];
	for (const iv of intervals) {
		const last = merged[merged.length - 1];
		if (last && iv.min <= last.max + adjacencyTolerance) {
			last.max = Math.max(last.max, iv.max);
		} else {
			merged.push({ ...iv });
		}
	}

	// Walk the data range [dataMin, dataMax] and find sub-ranges not covered by any merged interval
	const gaps = [];
	let cursor = dataMin;
	for (const iv of merged) {
		if (iv.max < cursor) continue;
		if (iv.min > cursor + adjacencyTolerance) {
			gaps.push({ min: cursor, max: Math.min(iv.min, dataMax) });
		}
		cursor = Math.max(cursor, iv.max);
		if (cursor >= dataMax) break;
	}
	if (cursor < dataMax - adjacencyTolerance) {
		gaps.push({ min: cursor, max: dataMax });
	}

	// Clip gaps to the data range and drop degenerate (zero-width) ones
	const clippedGaps = gaps
		.map(g => ({ min: Math.max(g.min, dataMin), max: Math.min(g.max, dataMax) }))
		.filter(g => g.max > g.min);

	const totalRange = dataMax - dataMin;
	const gapWidth = clippedGaps.reduce((sum, g) => sum + (g.max - g.min), 0);
	const coveragePercent =
		totalRange > 0 ? Math.max(0, Math.round((1 - gapWidth / totalRange) * 100)) : 100;

	return {
		hasGaps: clippedGaps.length > 0,
		gaps: clippedGaps,
		coveragePercent,
	};
}

/**
 * Computes per-band coverage gaps for a Matrix (2D) filter.
 *
 * For each band row, classifyWithMatrixFilter walks threshold.boundaries in
 * order and returns the first class whose boundary is >= the threshold
 * value. Two situations both silently fall through to "unclassified" even
 * though the underlying data is completely valid:
 *   1. threshold.max is defined and the real data for this row goes higher
 *      than it (the explicit max check in classifyWithMatrixFilter fires).
 *   2. The last boundary is lower than the row's effective ceiling, so
 *      values between the last boundary and that ceiling never match any
 *      class boundary (falls through to the "above all boundaries" return).
 *
 * The effective ceiling for each row is determined by comparing the row's
 * configured threshold.max to the *per-band* observed maximum (the highest
 * threshold value actually seen among features assigned to that band via
 * nearest-neighbor). Using per-band observed data avoids false positives
 * where a high value in one band inflates the ceiling of another band.
 *
 * Gap detection covers three cases per band:
 *   a) Last boundary < effective ceiling: values between the last boundary
 *      and the ceiling silently become "unclassified".
 *   b) threshold.max is set but the observed per-band max exceeds it:
 *      values above threshold.max are explicitly classified as
 *      "unclassified" by classifyWithMatrixFilter — this is reported as an
 *      overflow gap so the user is aware real data exceeds the configured
 *      range.
 *   c) threshold.max is unset and the observed per-band max exceeds the
 *      last boundary: same as (a) but inferred from observed data.
 *
 * Values below `threshold.min` are intentionally excluded: they're already
 * captured by the distinct, user-visible "Below Minimum" category rather
 * than being silently merged into "Unclassified", so they don't share the
 * same "invisible to the user" problem this check is meant to catch.
 *
 * Band assignment itself (nearest-neighbor over `filter.bands`) always
 * resolves to *some* band for any non-null band value, so — unlike the
 * threshold axis — there is no equivalent "band gap" concept to detect.
 *
 * @param {Object} filter - Matrix filter configuration (bands, thresholds, classes)
 * @param {Array<{min: number, max: number, hasData: boolean}>|{min: number, max: number, hasData: boolean}} perBandRanges -
 *   Per-band observed threshold ranges (one entry per band index), as
 *   computed by usePerBandPropertyRanges. For backwards compatibility a
 *   single global range object is also accepted (legacy behavior).
 * @param {number} [adjacencyTolerance=1] - Max distance between the last
 *   boundary and the ceiling to still be treated as fully covered
 * @returns {{
 *   hasGaps: boolean,
 *   gapsByBand: Array<{ bandIndex: number, band: number, min: number, max: number }>
 * }}
 */
export function getMatrixFilterCoverageGaps(filter, perBandRanges, adjacencyTolerance = 1) {
	if (!filter?.bands?.length || !filter?.thresholds?.length) {
		return { hasGaps: false, gapsByBand: [] };
	}

	// Support legacy call signature: a single global range object instead of per-band array.
	const isLegacyGlobalRange =
		perBandRanges && !Array.isArray(perBandRanges) && 'hasData' in perBandRanges;
	const getBandRange = bandIndex => {
		if (isLegacyGlobalRange) return perBandRanges;
		return perBandRanges?.[bandIndex] ?? null;
	};

	const gapsByBand = [];

	filter.bands.forEach((band, bandIndex) => {
		const threshold = filter.thresholds[bandIndex];
		if (!threshold) return;

		const bandRange = getBandRange(bandIndex);
		const observedMax = bandRange?.hasData ? bandRange.max : null;

		// Determine the effective ceiling — the highest value that needs to be
		// classifiable for this band row.
		let ceiling;
		const hasConfiguredMax = threshold.max !== undefined && threshold.max !== null;

		if (hasConfiguredMax && !isLegacyGlobalRange && observedMax !== null) {
			// Per-band observed data available: use whichever is higher so we
			// detect overflow where real data exceeds the configured max.
			ceiling = Math.max(threshold.max, observedMax);
		} else if (hasConfiguredMax) {
			// Configured max present but no per-band observed data (or legacy
			// global range) — trust the configured max as the ceiling.
			ceiling = threshold.max;
		} else if (observedMax !== null) {
			// No configured max — fall back to observed data for this band.
			ceiling = observedMax;
		} else {
			// No configured max and no observed data — nothing to check.
			return;
		}

		const boundaries = threshold.boundaries || [];
		const lastBoundary = boundaries.length ? boundaries[boundaries.length - 1] : undefined;
		const gapStart = lastBoundary ?? threshold.min ?? 0;

		if (ceiling - gapStart > adjacencyTolerance) {
			gapsByBand.push({ bandIndex, band, min: gapStart, max: ceiling });
		}
	});

	return {
		hasGaps: gapsByBand.length > 0,
		gapsByBand,
	};
}

/**
 * Describes what a filter classifies features by, in a single short,
 * human-readable string. Intended for contexts where classes/colors are
 * shown without the surrounding editor UI — e.g. an exported PDF/DOCX
 * report — so the reader knows what the colors represent instead of just
 * seeing color-coded classes with no context (see the "Simple (1D)" and
 * "Matrix (2D)" cases below).
 *
 * Property labels are resolved from `availableProperties` (the same
 * catalog the editor dropdowns use) and fall back to the raw property id
 * if a label can't be found — e.g. a saved template referencing a
 * property that no longer exists in the current dataset.
 *
 * @param {Object} filter - Current filter configuration (simple or matrix)
 * @param {Array<{id: string, label: string}>} availableProperties - Property catalog for label lookup
 * @returns {string} e.g. "Ha/eq (Total)" (simple) or
 *   "Mean Height (bands) × Density (thresholds)" (matrix)
 */
export function describeClassificationMethod(filter, availableProperties) {
	const labelFor = propertyId =>
		availableProperties?.find(p => p.id === propertyId)?.label || propertyId || 'N/A';

	if (filter?.type === 'simple') {
		return labelFor(filter.property);
	}

	return `${labelFor(filter?.bandProperty)} (bands) × ${labelFor(filter?.thresholdProperty)} (thresholds)`;
}

/**
 * Formats a coverage-gap result — from getSimpleFilterCoverageGaps or
 * getMatrixFilterCoverageGaps — into a single warning sentence suitable for
 * exported reports. Returns null when there are no gaps, so callers can
 * render conditionally (`{message && <p>{message}</p>}`) without a
 * separate `hasGaps` check.
 *
 * @param {Object} filter - Current filter configuration (used to tell simple vs matrix apart)
 * @param {Object} coverageGaps - Result of getSimpleFilterCoverageGaps or getMatrixFilterCoverageGaps
 * @returns {string|null}
 */
export function describeCoverageGaps(filter, coverageGaps) {
	if (!coverageGaps?.hasGaps) return null;

	if (filter?.type === 'simple') {
		const ranges = coverageGaps.gaps
			.map(g => `${g.min.toLocaleString()}–${g.max.toLocaleString()}`)
			.join(', ');
		return `Values in ${ranges} are not covered by any class and appear as Unclassified.`;
	}

	const ranges = (coverageGaps.gapsByBand || [])
		.map(g => `band ${g.band} (${g.min.toLocaleString()}–${g.max.toLocaleString()})`)
		.join(', ');
	if (!ranges) return null;
	return `Class boundaries don't cover the full threshold range for ${ranges} — affected values appear as Unclassified.`;
}
